// Utils
import { DateTime } from "luxon";

// Constants
import { ALL_ZERO_PHIN } from "Constants/PatientConstants";

// Normalizes health card numbers by removing spaces and dashes
export const normalizeHealthCard = (value) =>
  (value || "").replace(/[\s-]/g, "");

// Checks if a health card number is the all-zero PHIN.
export const isAllZeroPhin = (healthCardNumber) => {
  if (!healthCardNumber) {
    return false;
  }

  const spaceCount = healthCardNumber.match(/\s/g)?.length ?? 0;
  const dashCount = healthCardNumber.match(/-/g)?.length ?? 0;
  const normalized = normalizeHealthCard(healthCardNumber);

  return normalized === ALL_ZERO_PHIN && spaceCount <= 4 && dashCount <= 2;
};

// Normalizes last names for search by trimming, removing accents, non-letter characters, and converting to lowercase
export const normalizeLastNameForSearch = (value) =>
  String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}]/gu, "")
    .toLowerCase();

export const normalizeDateOfBirthValue = (value) => {
  if (!value) {
    return null;
  }

  if (DateTime.isDateTime(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : null;
  }

  return null;
};

const normalizeNameFromScan = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/(^|[^\p{L}])\p{L}/gu, (char) => char.toUpperCase());

// ----------------------------
// Language normalization
// ----------------------------

export const normalizePreferredLanguage = (value) => {
  const normalized = value.trim().toLowerCase();

  if (["french/francais", "french", "francais", "fr"].includes(normalized)) {
    return "fr";
  }

  if (["english/anglais", "english", "anglais", "en"].includes(normalized)) {
    return "en";
  }

  return "";
};

// ----------------------------
// Scanned Health Card
// ----------------------------

export const normalizeScannedHealthCard = (scannedCard) => ({
  ...scannedCard,
  firstName: normalizeNameFromScan(scannedCard.firstName),
  lastName: normalizeNameFromScan(scannedCard.lastName),
  dob: scannedCard.dob.trim(),
  phin: normalizeHealthCard(scannedCard.phin),
  regNum: scannedCard.regNum.trim(),
  preferredLanguage: normalizePreferredLanguage(scannedCard.lang),
});

// ----------------------------
// Mapping to Patient Form
// ----------------------------

export const mapScannedCardToPatientValues = (scannedCard) => {
  const normalized = normalizeScannedHealthCard(scannedCard);

  return {
    firstName: normalized.firstName,
    lastName: normalized.lastName,
    dob: normalized.dob,
    healthCardNumber: normalized.phin,
    registrationNumber: normalized.regNum,
    preferredLanguage: normalized.preferredLanguage,
  };
};
